import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
// @mui
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  FormControlLabel,
  Switch,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tabs,
  Typography,
} from '@mui/material';
// hooks
import useTabs from '@/hooks/useTabs';
import useSettings from '@/hooks/useSettings';
import useTable from '@/hooks/useTable';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
// components
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import { TableHeadCustom, TableNoData } from '@/components/table';
// custom components
import {
  BookingsTableToolbar,
  BookingsListSkeletons,
  SessionsTableRow,
} from '../components';
import SessionsMap from '../components/sessions/SessionsMap';
// API
import { getBookingSession, getFilteredBookingSessionsPaginate } from '../api';
import { getSettingsData } from '@/features/app/api';
// routes
import { bookingsManagementPath } from '../routes/paths';
// redux
import { dispatch } from '@/stores';
import { resetReduxValues } from '@/features/bookings-management/slices';

import { consoleError } from '@/utils/consoleError';
// Adjust this according to your columns
import {
  getDeepLinkedSessionFilter,
  getDefaultSessionListSort,
  formatDurationFromHours,
  isTodaySessionBottomStatus,
  resolveBookingSessionBillableHours,
  SESSIONS_TABLE_HEAD,
  sessionMatchesSessionListFilter,
  shouldIncludeBookingSessionInSummary,
  shouldUseTodayBottomStatusOrdering,
  sortTodaySessionsWithBottomStatuses,
} from '../utils';

export default function SessionsList() {
  const { themeStretch } = useSettings();
  const { errorMsg } = useSnackbarMsg();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    currentTab: sessionFilter,
    onChangeTab: onChangeFilterStatus,
    setCurrentTab: setSessionFilter,
  } = useTabs('today');
  const sessionFilterRef = useRef(sessionFilter);
  const isTodayTab = sessionFilter === 'today';
  const todayBottomStatusLast =
    shouldUseTodayBottomStatusOrdering(sessionFilter);
  const sessionIdParam = searchParams.get('sessionID');
  const deepLinkedSessionId = Number(sessionIdParam);
  const hasDeepLinkedSessionId =
    !!sessionIdParam && Number.isFinite(deepLinkedSessionId);

  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [filterServiceId, setFilterServiceId] = useState<number>(0);
  const [filterStatusId, setFilterStatusId] = useState<number>(0);
  const [filterPaymentStatusId, setFilterPaymentStatusId] = useState<number>(0);
  const [filterServiceTypeId, setFilterServiceTypeId] = useState<number>(0);
  const [filterStartDate, setFilterStartDate] = useState<string | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<string | null>(null);

  const clearSessionIdParam = () => {
    if (!searchParams.has('sessionID')) return;
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('sessionID');
    setSearchParams(nextSearchParams);
  };

  const resetFilters = () => {
    clearSessionIdParam();
    setSearchFieldValue('');
    setFilterServiceId(0);
    setFilterServiceTypeId(0);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterStatusId(0);
    setFilterPaymentStatusId(0);
    setPage(0);
  };

  const {
    dense,
    page,
    order,
    orderBy,
    setOrder,
    setOrderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({
    defaultOrder: getDefaultSessionListSort('today').order,
    defaultOrderBy: getDefaultSessionListSort('today').orderBy,
    rowsPerPageStorageKey: 'bookings-management.sessions-list.rows-per-page',
  });

  const setDefaultSortForSessionFilter = useCallback(
    (nextSessionFilter: string) => {
      const defaultSort = getDefaultSessionListSort(nextSessionFilter);
      setOrder(defaultSort.order);
      setOrderBy(defaultSort.orderBy);
    },
    [setOrder, setOrderBy]
  );

  // SETTINGS
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => getSettingsData(),
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching settings data`);
    },
  });

  const { data: deepLinkedSession } = useQuery({
    queryKey: ['bookingSession', String(deepLinkedSessionId)],
    queryFn: () => getBookingSession(deepLinkedSessionId),
    enabled: hasDeepLinkedSessionId,
    refetchOnWindowFocus: false,
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching booking session`);
    },
  });

  // SESSION FILTER OPTIONS
  const sessionFilterOptions = [
    'previous',
    'today',
    'upcoming',
    'unassigned',
    'cancelled',
  ];
  const hasFilters =
    !!searchFieldValue ||
    filterServiceId !== 0 ||
    filterStatusId !== 0 ||
    filterPaymentStatusId !== 0 ||
    filterServiceTypeId !== 0 ||
    filterStartDate !== null ||
    filterEndDate !== null;

  const selectedSessionStatusValue = useMemo(() => {
    if (!filterStatusId) return undefined;

    const status = settingsData?.bookingSessionStatuses.find(
      (item) => item.id === filterStatusId
    );

    return status?.value || status?.display || undefined;
  }, [filterStatusId, settingsData?.bookingSessionStatuses]);

  const selectedPaymentStatusValue = useMemo(() => {
    if (!filterPaymentStatusId) return undefined;

    const status = settingsData?.bookingPaymentStatuses.find(
      (item) => item.id === filterPaymentStatusId
    );

    return status?.value || status?.display || undefined;
  }, [filterPaymentStatusId, settingsData?.bookingPaymentStatuses]);

  // FETCH SESSIONS (server-side filters + pagination)
  const { data: sessionsData, isLoading: isSessionsDataLoading } = useQuery({
    queryKey: [
      'bookingSessions',
      {
        sessionFilter,
        page,
        rowsPerPage,
        searchFieldValue,
        filterServiceId,
        filterServiceTypeId,
        filterStartDate,
        filterEndDate,
        filterStatusId,
        selectedSessionStatusValue,
        filterPaymentStatusId,
        selectedPaymentStatusValue,
        order,
        orderBy,
        todayBottomStatusLast,
      },
    ],
    queryFn: () =>
      getFilteredBookingSessionsPaginate({
        sessionFilter,
        page: page + 1, // API is 1-indexed
        limit: rowsPerPage,
        search: searchFieldValue || undefined,
        serviceId: filterServiceId,
        serviceTypeId: filterServiceTypeId,
        startDate: filterStartDate,
        endDate: filterEndDate,
        statusId: filterStatusId,
        statusValue: selectedSessionStatusValue,
        paymentStatusId: filterPaymentStatusId,
        paymentStatusValue: selectedPaymentStatusValue,
        order,
        orderBy,
        completedLast: todayBottomStatusLast,
      }),
    keepPreviousData: true,
    enabled: !!settingsData,
    refetchInterval: 20000, // refresh every 20 seconds
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching booking sessions`);
    },
  });

  const sessions = useMemo(() => sessionsData?.data ?? [], [sessionsData]);
  const displayedSessions = useMemo(() => {
    if (!deepLinkedSession) return sessions;

    const shouldShowDeepLinkedSession =
      hasDeepLinkedSessionId &&
      sessionMatchesSessionListFilter(deepLinkedSession, sessionFilter);

    if (
      !shouldShowDeepLinkedSession ||
      sessions.some((session) => session.id === deepLinkedSession.id)
    ) {
      return sessions;
    }

    return [deepLinkedSession, ...sessions];
  }, [deepLinkedSession, hasDeepLinkedSessionId, sessionFilter, sessions]);
  const orderedDisplayedSessions = useMemo(
    () =>
      isTodayTab
        ? sortTodaySessionsWithBottomStatuses(displayedSessions)
        : displayedSessions,
    [displayedSessions, isTodayTab]
  );
  const totalSessions = sessionsData?.total ?? 0;
  const pageSummarySessions = useMemo(
    () => orderedDisplayedSessions.filter(shouldIncludeBookingSessionInSummary),
    [orderedDisplayedSessions]
  );
  const pageSessionsTotalHours = useMemo(
    () =>
      pageSummarySessions.reduce(
        (totalHours, session) =>
          totalHours + resolveBookingSessionBillableHours(session),
        0
      ),
    [pageSummarySessions]
  );
  const pageSessionsTotalHoursLabel = formatDurationFromHours(
    pageSessionsTotalHours
  );

  const [nowTick, setNowTick] = useState(() => Date.now());
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    null
  );
  const bottomStatusDividerIndex = useMemo(() => {
    if (!isTodayTab) return -1;

    const firstBottomStatusIndex = orderedDisplayedSessions.findIndex(
      (session) => isTodaySessionBottomStatus(session.booking_session_status)
    );

    return firstBottomStatusIndex > 0 ? firstBottomStatusIndex : -1;
  }, [orderedDisplayedSessions, isTodayTab]);

  useEffect(() => {
    if (!isTodayTab) return undefined;
    setNowTick(Date.now());
    const interval = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(interval);
  }, [isTodayTab]);

  useEffect(() => {
    if (!hasDeepLinkedSessionId) return;

    setSelectedSessionId(deepLinkedSessionId);
    setPage(0);
  }, [deepLinkedSessionId, hasDeepLinkedSessionId, setPage]);

  useEffect(() => {
    if (!deepLinkedSession) return;

    if (sessionMatchesSessionListFilter(deepLinkedSession, sessionFilter)) {
      return;
    }

    const targetFilter = getDeepLinkedSessionFilter(deepLinkedSession);
    if (targetFilter !== sessionFilter) {
      setSessionFilter(targetFilter);
    }
  }, [deepLinkedSession, sessionFilter, setSessionFilter]);

  useEffect(() => {
    if (sessionFilterRef.current === sessionFilter) return;

    setDefaultSortForSessionFilter(sessionFilter);
    sessionFilterRef.current = sessionFilter;
  }, [sessionFilter, setDefaultSortForSessionFilter]);

  useEffect(() => {
    if (
      selectedSessionId !== null &&
      !orderedDisplayedSessions.some(
        (session) => session.id === selectedSessionId
      )
    ) {
      setSelectedSessionId(null);
    }
  }, [orderedDisplayedSessions, selectedSessionId]);

  const handleSelectSession = (sessionId: number) => {
    setSelectedSessionId(sessionId);
  };

  const isNotFound =
    !isSessionsDataLoading && totalSessions === 0 && hasFilters;

  const handleSearchField = (filterName: string) => {
    clearSessionIdParam();
    setSearchFieldValue(filterName);
    setPage(0);
  };

  const handleFilterDropdown = (event: React.ChangeEvent<HTMLInputElement>) => {
    clearSessionIdParam();
    const { name, value } = event.target;
    switch (name) {
      case 'status':
        setFilterStatusId(Number(value));
        break;
      case 'payment_status':
        setFilterPaymentStatusId(Number(value));
        break;
      case 'service':
        setFilterServiceId(Number(value));
        setFilterServiceTypeId(0);
        break;
      case 'service_type':
        setFilterServiceTypeId(Number(value));
        break;
      case 'start_date':
        setFilterStartDate(value);
        break;
      case 'end_date':
        setFilterEndDate(value);
        break;
      default:
        consoleError('SessionsList', 'handleFilterDropdown()');
    }
    setPage(0);
  };

  const handleDatepickerChange = (date: string | null, date_type: string) => {
    clearSessionIdParam();
    switch (date_type) {
      case 'start':
        setFilterStartDate(date);
        break;
      case 'end':
        setFilterEndDate(date);
        break;
      default:
        consoleError('SessionsList', 'handleDatepickerChange()');
    }
    setPage(0);
  };

  const handleSessionTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: string
  ) => {
    clearSessionIdParam();
    setDefaultSortForSessionFilter(newValue);
    sessionFilterRef.current = newValue;
    onChangeFilterStatus(event, newValue);
    setPage(0);
  };

  return (
    <Page title="Bookings Management">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Booking Sessions"
          links={[
            { name: 'Dashboard', href: '' },
            { name: 'Sessions', href: '' },
          ]}
          action={
            <Button
              variant="contained"
              component={RouterLink}
              to={bookingsManagementPath('new')}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={() => dispatch(resetReduxValues())}
              disabled={isSessionsDataLoading}
            >
              New Booking
            </Button>
          }
        />

        {/* TABLE  */}
        <Card>
          {isSessionsDataLoading ? (
            <BookingsListSkeletons />
          ) : (
            <>
              {isTodayTab && (
                <Box sx={{ px: 2, pt: 2 }}>
                  <SessionsMap
                    sessions={orderedDisplayedSessions}
                    selectedSessionId={selectedSessionId}
                    onSelectSession={handleSelectSession}
                    isLoading={isSessionsDataLoading}
                  />
                </Box>
              )}

              <Box
                sx={{
                  px: 2,
                  bgcolor: 'background.neutral',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Tabs
                  allowScrollButtonsMobile
                  variant="scrollable"
                  scrollButtons="auto"
                  value={sessionFilter}
                  onChange={handleSessionTabChange}
                  sx={{ flex: '1 1 auto', minWidth: 0 }}
                >
                  {sessionFilterOptions.map((tab, index) => (
                    <Tab disableRipple key={index} label={tab} value={tab} />
                  ))}
                </Tabs>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    gap: 1,
                    flexWrap: 'wrap',
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  >
                    <Iconify icon="eva:list-outline" width={18} />
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      Sessions: {totalSessions}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      color: 'text.secondary',
                      flexShrink: 0,
                    }}
                  >
                    <Iconify icon="basil:clock-outline" width={18} />
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      Page hours: {pageSessionsTotalHoursLabel}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              <BookingsTableToolbar
                filterService={filterServiceId}
                filterServiceType={filterServiceTypeId}
                filterSearchBar={searchFieldValue}
                filterStartDate={filterStartDate}
                filterEndDate={filterEndDate}
                filterStatusId={filterStatusId}
                filterPaymentStatusId={filterPaymentStatusId}
                handleFilterDropdown={handleFilterDropdown}
                handleSearchField={handleSearchField}
                handleDatepickerChange={handleDatepickerChange}
                resetFilters={resetFilters}
                settingsData={settingsData}
                showStatusFilters
              />

              <Scrollbar>
                <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
                  <Table size={dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={order}
                      orderBy={orderBy}
                      headLabel={SESSIONS_TABLE_HEAD}
                      rowCount={totalSessions}
                      numSelected={0} // if you have selection logic, you can handle it
                      onSort={onSort}
                    />
                    <TableBody>
                      {orderedDisplayedSessions.map((session, index) => (
                        <SessionsTableRow
                          key={session.id}
                          row={session}
                          showLiveStatus={isTodayTab}
                          referenceTime={nowTick}
                          highlightCompleted={isTodayTab}
                          showCompletedDivider={
                            index === bottomStatusDividerIndex
                          }
                          isSelected={selectedSessionId === session.id}
                          isExpanded={deepLinkedSessionId === session.id}
                          onSelectSession={handleSelectSession}
                        />
                      ))}

                      <TableNoData
                        isNotFound={isNotFound}
                        title="No sessions found"
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100, 150]}
                  component="div"
                  count={totalSessions}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={onChangePage}
                  onRowsPerPageChange={onChangeRowsPerPage}
                />

                <FormControlLabel
                  control={<Switch checked={dense} onChange={onChangeDense} />}
                  label="Dense"
                  sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
